import React, { useMemo, useRef, useEffect, useState, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { MdArrowBack, MdArrowForward } from "react-icons/md";
import { useLanguage } from "@/contexts/LanguageContext";
import GetStartedButton from "./GetStartedButton";
import { trustpilotLogoSmall } from "@/images";
import {
  trackReviewReadMore,
  trackReviewNext,
  trackReviewsScroll,
  trackReviewsCTA,
} from "@/components/analytics/reviewsEvents";

// Fallback translations
const fallbackTranslations = {
  en: {
    "reviews.table.rating": "Rating",
    "reviews.table.author": "Author",
    "reviews.table.review": "Review",
    "reviews.table.readMore": "Read More",
    "reviews.table.noReviews": "No reviews yet",
    "reviews.table.checkBackLater": "Check back later for user reviews",
    "reviews.table.verified": "Verified",
    "reviews.table.verifiedBy": "Verified by:",
    "reviews.table.from": "From:",
    "reviews.overlay.backToReviews": "Back to Reviews",
    "reviews.overlay.nextReview": "Next Review",
    "reviews.cta.getStarted": "Get Started Today!",
  },
  es: {
    "reviews.table.rating": "Calificación",
    "reviews.table.author": "Autor",
    "reviews.table.review": "Reseña",
    "reviews.table.readMore": "Leer Más",
    "reviews.table.noReviews": "Aún no hay reseñas",
    "reviews.table.checkBackLater": "Vuelve más tarde para ver las reseñas de usuarios",
    "reviews.table.verified": "Verificado",
    "reviews.table.verifiedBy": "Verificado por:",
    "reviews.table.from": "De:",
    "reviews.overlay.backToReviews": "Volver a Reseñas",
    "reviews.overlay.nextReview": "Siguiente Reseña",
    "reviews.cta.getStarted": "¡Comienza Hoy!",
  },
};

const createFallbackT = (lang = "en") => (key) => {
  const translations = fallbackTranslations[lang] || fallbackTranslations.en;
  return translations[key] || fallbackTranslations.en[key] || key;
};

const columnHelper = createColumnHelper();

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-yellow-500 font-semibold">{rating}</span>
      <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    </div>
  );
}

function AuthorCell({ name, location, isVerified, t }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] sm:text-xs text-text-primary font-medium">
        {name}{location && `, ${location}`}
      </span>
      <div className="flex flex-wrap items-center gap-1 mt-1">
        <span className="text-[10px] text-text-muted">
          {isVerified ? (<><span className="min-[425px]:hidden">{t("reviews.table.verified")}:</span><span className="hidden min-[425px]:inline">{t("reviews.table.verifiedBy")}</span></>) : t("reviews.table.from")}
        </span>
        <img
          src={trustpilotLogoSmall}
          alt="Trustpilot"
          className="h-3 w-auto object-contain max-[424px]:mt-0.5"
        />
      </div>
    </div>
  );
}

function ReviewOverlay({ review, onClose, onNext, onPrevious, hasNext, hasPrevious, affiliateLink, companyName, t }) {
  const { language } = useLanguage();
  if (!review) return null;

  const handleNext = () => {
    trackReviewNext(companyName);
    onNext();
  };

  const handleCTAClose = () => {
    trackReviewsCTA(companyName, "overlay");
    onClose();
  };

  return (
    <Dialog open={!!review} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col p-2 sm:p-6">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto py-4 min-h-0">
          <div className="space-y-4">
            {/* Rating */}
            <StarRating rating={review.starRating} />

            {/* Author */}
            <AuthorCell
              name={review.reviewAuthor}
              location={review.reviewLocation}
              isVerified={review.isVerified}
              t={t}
            />

            {/* Title */}
            {review.reviewTitle && (
              <h3 className="text-lg font-semibold text-text-primary">
                {review.reviewTitle}
              </h3>
            )}

            {/* Review Text */}
            <div>
              <p className="text-sm text-text-primary whitespace-pre-wrap">
                {review.reviewText}
              </p>
              {review.reviewDate && (
                <p className="text-xs text-text-muted mt-3">
                  {new Date(review.reviewDate).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Navigation and CTA - Fixed at Bottom */}
        <div className="pt-4 border-t border-border-light mt-auto space-y-3">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
            >
              <MdArrowBack className="w-5 h-5" />
              <span className="text-sm font-medium">{t("reviews.overlay.backToReviews")}</span>
            </button>

            {hasNext && (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 text-brand-purple hover:text-brand-purple-dark transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium">{t("reviews.overlay.nextReview")}</span>
                <MdArrowForward className="w-5 h-5" />
              </button>
            )}
          </div>
          <GetStartedButton
            affiliateLink={affiliateLink}
            onClose={handleCTAClose}
            t={t}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ReviewsTable({ reviews, onScroll, affiliateLink, companyName, t: tProp }) {
  const { language } = useLanguage();
  const t = tProp || createFallbackT(language);
  const scrollRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState(null);

  const selectedReview = selectedReviewIndex !== null ? reviews[selectedReviewIndex] : null;

  const handleNext = () => {
    if (selectedReviewIndex !== null && selectedReviewIndex < reviews.length - 1) {
      setSelectedReviewIndex(selectedReviewIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedReviewIndex !== null && selectedReviewIndex > 0) {
      setSelectedReviewIndex(selectedReviewIndex - 1);
    }
  };

  const handleReadMore = useCallback((row) => {
    const index = reviews.findIndex(r => r === row);
    setSelectedReviewIndex(index);
    trackReviewReadMore(companyName, row.reviewAuthor);
  }, [reviews, companyName]);

  const hasNext = selectedReviewIndex !== null && selectedReviewIndex < reviews.length - 1;
  const hasPrevious = selectedReviewIndex !== null && selectedReviewIndex > 0;

  // Handle scroll event with throttling
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    let scrollTimeout;
    const handleScrollEvent = () => {
      // Only track the first scroll event to avoid spam
      if (!hasScrolledRef.current) {
        hasScrolledRef.current = true;
        onScroll?.();
        trackReviewsScroll(companyName);
      }

      // Reset after 2 seconds of no scrolling
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        hasScrolledRef.current = false;
      }, 2000);
    };

    scrollElement.addEventListener("scroll", handleScrollEvent);
    return () => {
      scrollElement.removeEventListener("scroll", handleScrollEvent);
      clearTimeout(scrollTimeout);
    };
  }, [onScroll, companyName]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("starRating", {
        header: () => (
          <>
            <span className="hidden sm:inline">{t("reviews.table.rating")}</span>
            <svg className="w-4 h-4 text-yellow-500 sm:hidden" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </>
        ),
        cell: (info) => <StarRating rating={info.getValue()} />,
        size: 56,
      }),
      columnHelper.accessor("reviewAuthor", {
        header: t("reviews.table.author"),
        cell: (info) => {
          const row = info.row.original;
          return (
            <AuthorCell
              name={info.getValue()}
              location={row.reviewLocation}
              isVerified={row.isVerified}
              t={t}
            />
          );
        },
        size: 150,
      }),
      columnHelper.accessor("reviewText", {
        header: t("reviews.table.review"),
        cell: (info) => {
          const row = info.row.original;
          const reviewText = info.getValue();
          const reviewDate = row.reviewDate;
          const maxLength = 200;
          const isTruncated = reviewText && reviewText.length > maxLength;
          const displayText = isTruncated
            ? reviewText.slice(0, maxLength).trim() + "..."
            : reviewText;

          return (
            <div>
              <p className="text-xs sm:text-sm text-text-secondary">
                {displayText}
                {isTruncated && (
                  <button
                    onClick={() => handleReadMore(row)}
                    className="text-brand-purple hover:underline cursor-pointer ml-1"
                  >
                    {t("reviews.table.readMore")}
                  </button>
                )}
              </p>
              {reviewDate && (
                <p className="text-[10px] sm:text-xs text-text-muted mt-2">
                  {new Date(reviewDate).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          );
        },
        size: 500,
      }),
    ],
    [reviews, handleReadMore, t, language]
  );

  const table = useReactTable({
    data: reviews,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Empty state
  if (reviews.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-text-secondary text-lg">{t("reviews.table.noReviews")}</p>
          <p className="text-text-muted text-sm mt-1">{t("reviews.table.checkBackLater")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ReviewOverlay
        review={selectedReview}
        onClose={() => setSelectedReviewIndex(null)}
        onNext={handleNext}
        onPrevious={handlePrevious}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        affiliateLink={affiliateLink}
        companyName={companyName}
        t={t}
      />
      <div
        ref={scrollRef}
        className="overflow-auto h-full rounded-lg border border-border-light"
      >
        <table className="w-full border-collapse">
          <thead className="bg-surface-light sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-2 py-2 sm:px-4 sm:py-3 text-left text-xs font-semibold text-text-primary uppercase tracking-wider border-b border-border-light"
                    style={{ width: header.column.columnDef.size }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-border-light">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="hover:bg-surface-light transition-colors"
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-2 py-2 sm:px-4 sm:py-4 text-sm"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
